import PaystackPop from "@paystack/inline-js";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";
import { GlobalContext } from "../../context";
import { currencyFormatter } from "../../utility";

// ─── Step indicator ──────────────────────────────────────────────────────────

function Steps({ current }) {
  const labels = ["Browse", "Select Course", "Payment Plan", "Pay"];
  return (
    <div className="flex items-center gap-0 mb-8">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                  ${done ? "bg-[#F05A19] text-white" : active ? "bg-[#F05A19] text-white ring-4 ring-[#F05A19]/20" : "bg-gray-100 text-gray-400"}`}
              >
                {done ? "✓" : step}
              </div>
              <span className={`text-xs whitespace-nowrap hidden sm:block ${active ? "text-[#F05A19] font-medium" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${done ? "bg-[#F05A19]" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Browse cohorts ───────────────────────────────────────────────────

function BrowseCohorts({ cohorts, onSelect }) {
  if (cohorts.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-4xl mb-3">🎓</p>
        <p className="font-medium text-gray-600">No open cohorts at the moment</p>
        <p className="text-sm mt-1">Check back soon — new cohorts are added regularly.</p>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cohorts.map((cohort) => {
        const start = cohort.start_date
          ? new Date(cohort.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : null;
        const end = cohort.end_date
          ? new Date(cohort.end_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : null;

        return (
          <button
            key={cohort.id}
            onClick={() => onSelect(cohort)}
            className="text-left bg-white rounded-xl border-2 border-gray-200 hover:border-[#F05A19]
              p-5 transition-all duration-200 hover:shadow-md group"
          >
            <div className="h-1.5 bg-gradient-to-r from-[#F05A19] to-[#ff8c5a] rounded-full mb-4" />
            <h3 className="font-semibold text-gray-800 text-base mb-2 group-hover:text-[#F05A19] transition-colors">
              {cohort.name}
            </h3>
            {(start || end) && (
              <p className="text-xs text-gray-500 mb-3">
                {start && `Starts ${start}`}
                {start && end && " · "}
                {end && `Ends ${end}`}
              </p>
            )}
            <p className="text-xs text-gray-400">
              {cohort.courses?.length || 0} course{cohort.courses?.length !== 1 ? "s" : ""} available
            </p>
            <div className="mt-3 text-xs font-medium text-[#F05A19] flex items-center gap-1">
              View courses →
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Step 2: Select course ────────────────────────────────────────────────────

function SelectCourse({ cohort, currency, onSelect, onBack }) {
  const isNG = currency === "NGN";
  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1">
        ← Back to cohorts
      </button>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800">{cohort.name}</h2>
        <p className="text-sm text-gray-500">Choose the course you want to enroll in</p>
      </div>

      {cohort.courses?.length === 0 ? (
        <p className="text-gray-400 text-sm">No courses available in this cohort.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {cohort.courses.map((course) => (
            <button
              key={course.uuid || course.id}
              onClick={() => onSelect(course)}
              className="text-left bg-white rounded-xl border-2 border-gray-200 hover:border-[#F05A19]
                overflow-hidden transition-all duration-200 hover:shadow-md group"
            >
              {course.image_url && (
                <div className="h-36 overflow-hidden">
                  <img
                    src={course.image_url}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 text-sm mb-1 group-hover:text-[#F05A19] transition-colors">
                  {course.title}
                </h3>
                {course.description && (
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{course.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-[#F05A19]">
                    {isNG
                      ? currencyFormatter(course.price_ngn, { currency: "NGN" })
                      : currencyFormatter(course.price_usd, { currency: "USD" })}
                  </span>
                  <span className="text-xs text-gray-400">
                    {isNG
                      ? currencyFormatter(course.price_usd, { currency: "USD" })
                      : currencyFormatter(course.price_ngn, { currency: "NGN" })}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Choose installment plan ─────────────────────────────────────────

function ChoosePlan({ cohort, course, currency, onSelect, onBack }) {
  const [selected, setSelected] = useState(null);
  const options = course.installment_options
    ? Object.values(course.installment_options)
    : [];

  const amtKey = currency === "NGN" ? "amount_ngn" : "amount_usd";
  const symbol = currency === "NGN" ? "NGN" : "USD";

  const planLabels = {
    1: { label: "Full Payment", sub: "Pay once and save", badge: "Best Value" },
    2: { label: "2 Installments", sub: "Split into 2 payments", badge: null },
    3: { label: "3 Installments", sub: "Split into 3 payments", badge: "Most Flexible" },
  };

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1">
        ← Back to course selection
      </button>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800">Choose your payment plan</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Enrolling in <strong>{course.title}</strong> — {cohort.name}
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {options.map((opt) => {
          const plan = planLabels[opt.installments] || { label: `${opt.installments} Installments`, sub: "", badge: null };
          const isSelected = selected?.installments === opt.installments;
          return (
            <button
              key={opt.installments}
              onClick={() => setSelected(opt)}
              className={`text-left rounded-xl border-2 p-5 transition-all duration-200 relative
                ${isSelected
                  ? "border-[#F05A19] bg-[#FEF0EB] shadow-md"
                  : "border-gray-200 bg-white hover:border-[#F05A19]/50 hover:shadow-sm"}`}
            >
              {plan.badge && (
                <span className="absolute top-3 right-3 text-xs bg-[#F05A19] text-white px-2 py-0.5 rounded-full">
                  {plan.badge}
                </span>
              )}
              <p className="font-semibold text-gray-800 text-sm mb-0.5">{plan.label}</p>
              <p className="text-xs text-gray-500 mb-3">{plan.sub}</p>
              <p className="text-xl font-bold text-[#F05A19]">
                {currencyFormatter(opt[amtKey], { currency: symbol })}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {opt.installments === 1 ? "total" : `per installment`}
              </p>
              {opt.installments > 1 && (
                <p className="text-xs text-gray-400 mt-1">
                  Total: {currencyFormatter(opt[amtKey] * opt.installments, { currency: symbol })}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Access tier info */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm">
        <p className="font-medium text-gray-700 mb-2">What you get with each payment level</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
          <span>✅ Live classes — any payment</span>
          <span>✅ Projects — any payment</span>
          <span>🔒 Assessments — 60%+ paid</span>
          <span>🔒 Recordings — 100% paid</span>
          <span className="col-span-2">🎓 Certificate — 100% + approved</span>
        </div>
      </div>

      <button
        disabled={!selected}
        onClick={() => selected && onSelect(selected)}
        className="w-full bg-[#F05A19] hover:bg-[#d14d14] text-white py-3 rounded-xl
          font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {selected
          ? `Continue — Pay ${currencyFormatter(selected[amtKey], { currency: symbol })}`
          : "Select a plan to continue"}
      </button>
    </div>
  );
}

// ─── Step 4: Confirm & pay ────────────────────────────────────────────────────

function ConfirmPay({ cohort, course, plan, currency, onBack, onPay, paying }) {
  const amtKey = currency === "NGN" ? "amount_ngn" : "amount_usd";
  const symbol = currency === "NGN" ? "NGN" : "USD";

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1">
        ← Change plan
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Order Summary</h2>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Cohort</span>
            <span className="font-medium text-gray-800">{cohort.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Course</span>
            <span className="font-medium text-gray-800 text-right max-w-xs">{course.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Plan</span>
            <span className="font-medium text-gray-800">
              {plan.installments === 1 ? "Full Payment" : `${plan.installments} Installments`}
            </span>
          </div>
          <div className="border-t border-gray-100 pt-3 flex justify-between">
            <span className="text-gray-700 font-medium">Due now</span>
            <span className="font-bold text-[#F05A19] text-lg">
              {currencyFormatter(plan[amtKey], { currency: symbol })}
            </span>
          </div>
          {plan.installments > 1 && (
            <div className="flex justify-between text-xs text-gray-400">
              <span>Total across {plan.installments} installments</span>
              <span>{currencyFormatter(plan[amtKey] * plan.installments, { currency: symbol })}</span>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-4 text-center">
        You'll be redirected to Paystack to complete your payment securely.
      </p>

      <button
        onClick={onPay}
        disabled={paying}
        className="w-full bg-[#F05A19] hover:bg-[#d14d14] text-white py-3.5 rounded-xl
          font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed
          flex items-center justify-center gap-2"
      >
        {paying ? (
          <>
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Initializing payment…
          </>
        ) : (
          `Pay ${currencyFormatter(plan[amtKey], { currency: symbol })} via Paystack`
        )}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CohortEnrollment({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    authState,
    authState: { loggedIn },
    userLocation,
  } = useContext(GlobalContext);

  const currency = userLocation?.data === "NG" ? "NGN" : "USD";

  // If navigated from a course card, pre-select cohort+course and jump to plan step
  const preSelected = location.state;

  const [step, setStep] = useState(preSelected?.cohort && preSelected?.course ? 3 : 1);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCohort, setSelectedCohort] = useState(preSelected?.cohort || null);
  const [selectedCourse, setSelectedCourse] = useState(preSelected?.course || null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    client
      .get("/cohorts")
      .then((res) => {
        const list = res.data?.cohorts || res.data?.data || res.data || [];
        setCohorts(Array.isArray(list) ? list : []);
      })
      .catch(() => toast.error("Could not load cohorts."))
      .finally(() => setLoading(false));
  }, []);

  const handleCohortSelect = (cohort) => {
    setSelectedCohort(cohort);
    setStep(2);
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setStep(3);
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setStep(4);
  };

  const handlePay = async () => {
    if (!loggedIn) {
      toast.info("Please sign in to enroll.");
      navigate("/sign-in");
      return;
    }

    setPaying(true);
    try {
      const res = await client.post(`/cohorts/${selectedCohort.id}/enroll`, {
        course_uuid: selectedCourse?.uuid,
        installments: selectedPlan.installments,
        currency,
      });

      const accessCode = res.data?.access_code;
      if (!accessCode) {
        toast.error("Could not initialize payment. Please try again.");
        return;
      }

      const amtKey = currency === "NGN" ? "amount_ngn" : "amount_usd";
      const popup = new PaystackPop();
      popup.newTransaction({
        key: process.env.REACT_APP_PAYSTACK_KEY,
        access_code: accessCode,
        email: authState?.data?.user?.email,
        amount: Math.round(selectedPlan[amtKey] * 100),
        currency,
        onSuccess: (response) => {
          navigate(`/checkout/callback?reference=${response.reference}`);
        },
        onCancel: () => {
          toast.info("Payment cancelled.");
          setPaying(false);
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Enrollment failed. Please try again.";
      toast.error(msg);
      setPaying(false);
    }
  };

  const inner = (
    <>
      <Steps current={step} />
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#F05A19] rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {step === 1 && (
            <BrowseCohorts cohorts={cohorts} onSelect={handleCohortSelect} />
          )}
          {step === 2 && selectedCohort && (
            <SelectCourse
              cohort={selectedCohort}
              currency={currency}
              onSelect={handleCourseSelect}
              onBack={() => { setSelectedCohort(null); setStep(1); }}
            />
          )}
          {step === 3 && selectedCohort && selectedCourse && (
            <ChoosePlan
              cohort={selectedCohort}
              course={selectedCourse}
              currency={currency}
              onSelect={handlePlanSelect}
              onBack={() => { setSelectedPlan(null); setStep(2); }}
            />
          )}
          {step === 4 && selectedCohort && selectedCourse && selectedPlan && (
            <ConfirmPay
              cohort={selectedCohort}
              course={selectedCourse}
              plan={selectedPlan}
              currency={currency}
              onBack={() => { setSelectedPlan(null); setStep(3); }}
              onPay={handlePay}
              paying={paying}
            />
          )}
        </>
      )}
    </>
  );

  if (embedded) {
    return <div className="py-6">{inner}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <h1 className="text-2xl font-bold text-gray-900">Cohort Enrollment</h1>
          <p className="text-sm text-gray-500 mt-1">
            Join a structured learning cohort with live sessions, projects, and a certificate.
          </p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {inner}
      </div>
    </div>
  );
}
